import { AppBreadCrumbTemplate } from '@/app/AppBreadCrumbTemplate';
import { Account } from '@/client/accounts';
import { Integrations } from '@/client/accounts/types/account-user-management.type';
import { RedirectPaths, RedirectPathsEnum } from '@/common/constants';
import { TranslationFunctionType } from '@/common/types';
import { AccountCard } from '@/components/accounts/AccountCard';
import { IntegrationsLockPanel } from '@/components/accounts/IntegrationsLockPanel';
import { useAccount } from '@/hooks/query';
import { useAppSelector } from '@/hooks/store';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { selectCurrentAccount } from '@/store/features/account';
import { AppBreadCrumb } from '@/ui/breadcrumb';
import { AppButton } from '@/ui/buttons';
import { FlexContainer } from '@/ui/styled-ui';
import { getAccountIntegration } from '@/utils/helpers';
import { PostHogFeatureFlags } from '@/utils/posthog';
import { isTrialRestricted } from '@/utils/posthog/posthog.helper';
import { MenuItem } from 'primereact/menuitem';
import { ProgressSpinner } from 'primereact/progressspinner';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';
import { AccountHeader } from './AccountHeader';

const StyledCardsWrapper = styled(FlexContainer)`
  width: 100%;
  justify-content: flex-start;
  align-items: center;
  gap: var(--small-padding);
  padding-bottom: var(--default-padding);
  flex-wrap: wrap;
`;

const getPathItems = (
  account: Account,
  currentAccount: Account,
  t: TranslationFunctionType,
): MenuItem[] => {
  const items: MenuItem[] = [
    {
      label: currentAccount?.name,
      url: RedirectPaths[RedirectPathsEnum.EDIT_ACCOUNT](currentAccount?.id),
      template: AppBreadCrumbTemplate,
    },
  ];

  if (currentAccount?.isSystem && account) {
    items.push(
      {
        label: t('accounts'),
        url: RedirectPaths[RedirectPathsEnum.ACCOUNTS](),
        template: AppBreadCrumbTemplate,
      },
      {
        label: account?.name,
        url: !currentAccount?.isSystem
          ? RedirectPaths[RedirectPathsEnum.ACCOUNT](account?.id)
          : RedirectPaths[RedirectPathsEnum.EDIT_ACCOUNT](account?.id),
        className: 'active',
        template: AppBreadCrumbTemplate,
      },
    );
  }
  items.push({
    label: t('integrations'),
    url: !currentAccount?.isSystem
      ? RedirectPaths[RedirectPathsEnum.ACCOUNT_INTEGRATIONS]()
      : RedirectPaths[RedirectPathsEnum.ACCOUNTS_INTEGRATIONS](account?.id),
    template: AppBreadCrumbTemplate,
  });

  return items;
};

export const AccountIntegrationsPage: React.FC = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const currentAccount = useAppSelector(selectCurrentAccount);
  const { canUseFeature, accountCanUseFeature } = useFeatureFlag();
  const { account, isLoading } = useAccount({
    accountId: currentAccount?.isSystem ? id : currentAccount?.id,
  });
  const navigate = useNavigate();

  const accountId = currentAccount?.isSystem ? id : currentAccount?.id;
  const viewedAccount = currentAccount?.isSystem ? account : currentAccount;

  const isTrialLocked =
    !!viewedAccount &&
    isTrialRestricted(viewedAccount) &&
    (canUseFeature(PostHogFeatureFlags.FREE_TRIAL_PLATFORM_RESTRICTIONS) ||
      accountCanUseFeature(
        PostHogFeatureFlags.FREE_TRIAL_PLATFORM_RESTRICTIONS,
        viewedAccount?.featureFlagAbility,
      ));

  const currentAccountIntegrationSource = isTrialLocked
    ? undefined
    : getAccountIntegration(viewedAccount)?.source;

  switch (currentAccountIntegrationSource) {
    case Integrations.ACTIVE_DIRECTORY: {
      navigate(
        !currentAccount?.isSystem
          ? RedirectPaths[RedirectPathsEnum.ACCOUNT_INTEGRATIONS_MICROSOFT]()
          : RedirectPaths[RedirectPathsEnum.ACCOUNTS_INTEGRATIONS_MICROSOFT](
              accountId as string,
            ),
      );
      break;
    }
    case Integrations.GOOGLE_WORKSPACE: {
      navigate(
        !currentAccount?.isSystem
          ? RedirectPaths[RedirectPathsEnum.ACCOUNT_INTEGRATIONS_GOOGLE]()
          : RedirectPaths[RedirectPathsEnum.ACCOUNTS_INTEGRATIONS_GOOGLE](
              accountId as string,
            ),
      );
      break;
    }
    default:
      break;
  }

  return (
    <>
      {isLoading || !account || !!currentAccountIntegrationSource ? (
        <FlexContainer direction="column" className="mt-5">
          <ProgressSpinner />
        </FlexContainer>
      ) : (
        <>
          {account && currentAccount && (
            <>
              <AppBreadCrumb model={getPathItems(account, currentAccount, t)} />
              <AccountHeader
                selectedAccount={account}
                title={t('integrations')}
              />
            </>
          )}

          {isTrialLocked ? (
            <IntegrationsLockPanel />
          ) : (
            <StyledCardsWrapper className="account-integrations-container">
              <AccountCard
                title={t('account.microsoft')}
                content={t('account.microsoft.intergration.description')}
                footer={
                  <AppButton
                    type="outlined"
                    label={t('generic.add')}
                    className="w-full mt-auto"
                    onClick={() =>
                      navigate(
                        !currentAccount?.isSystem
                          ? RedirectPaths[
                              RedirectPathsEnum.ACCOUNT_INTEGRATIONS_MICROSOFT
                            ]()
                          : RedirectPaths[
                              RedirectPathsEnum.ACCOUNTS_INTEGRATIONS_MICROSOFT
                            ](accountId as string),
                      )
                    }
                  />
                }
              />
              <span>{t('generic.or')}</span>
              <AccountCard
                title={t('account.google')}
                content={t('account.google.intergration.description')}
                footer={
                  <AppButton
                    type="outlined"
                    label={t('generic.add')}
                    className="w-full mt-auto"
                    onClick={() =>
                      navigate(
                        !currentAccount?.isSystem
                          ? RedirectPaths[
                              RedirectPathsEnum.ACCOUNT_INTEGRATIONS_GOOGLE
                            ]()
                          : RedirectPaths[
                              RedirectPathsEnum.ACCOUNTS_INTEGRATIONS_GOOGLE
                            ](accountId as string),
                      )
                    }
                  />
                }
              />
            </StyledCardsWrapper>
          )}
        </>
      )}
    </>
  );
};
